import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import SkinAssessment from "@/components/SkinAssessment";
import Navbar from "@/components/Navbar";
import ScoreBreakdown from "@/components/ScoreBreakdown";
import StreakWidget from "@/components/StreakWidget";
import { 
  Sun, Moon, Plus, RefreshCw, Sparkles, Archive, AlertTriangle,
  ChevronRight, Lightbulb, Package, Clock, CheckCircle2, PackageX,
  Target, Settings2, Droplets, RotateCcw
} from "lucide-react";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, token, updateUser } = useAuth();
  const [routine, setRoutine] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showSkinAssessment, setShowSkinAssessment] = useState(false);
  const [skinProfile, setSkinProfile] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [loadingRecs, setLoadingRecs] = useState(false);

  useEffect(() => {
    fetchData();
    fetchSkinProfile();
    // Show skin assessment if not set
    if (user && !user.skin_type) {
      setShowSkinAssessment(true);
    }
  }, [user]);

  // Fetch recommendations after main data loads
  useEffect(() => {
    if (!loading && products.length > 0) {
      fetchRecommendations();
    }
  }, [loading, products.length]);

  const fetchData = async () => {
    try {
      const [routineRes, productsRes] = await Promise.all([
        axios.get(`${API}/routine`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/products`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setRoutine(routineRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSkinProfile = async () => {
    try {
      const response = await axios.get(`${API}/auth/skin-profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSkinProfile(response.data);
    } catch (error) {
      console.error("Error fetching skin profile:", error);
    }
  };

  const fetchRecommendations = async () => {
    setLoadingRecs(true);
    try {
      const response = await axios.get(`${API}/recommendations?limit=3`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecommendations(response.data.recommendations || []);
    } catch (error) {
      console.error("Error fetching recommendations:", error);
    } finally {
      setLoadingRecs(false);
    }
  };

  const generateRoutine = async () => {
    if (products.length === 0) {
      toast.error("Add products first to generate a routine");
      navigate("/add-product");
      return;
    }
    
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/routine/generate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoutine(response.data);
      toast.success("Routine generated successfully!");
      // Refresh products to get updated statuses
      const productsRes = await axios.get(`${API}/products`, { headers: { Authorization: `Bearer ${token}` } });
      setProducts(productsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate routine");
    } finally {
      setGenerating(false);
    }
  };

  const handleSkinAssessmentComplete = async ({ skinType, concerns }) => {
    updateUser({ skin_type: skinType });
    setShowSkinAssessment(false);
    // Refresh skin profile
    fetchSkinProfile();
    // Regenerate routine with new skin profile
    if (products.length > 0 && routine) {
      toast.info("Regenerating routine with your new skin profile...");
      generateRoutine();
    }
  };

  // Skin Profile Card Component
  const SkinProfileCard = () => {
    if (!skinProfile) return null;
    
    const getSkinTypeIcon = (type) => {
      const icons = {
        dry: "🌞",
        oily: "💧",
        combination: "☁️",
        normal: "✨",
        sensitive: "🌸"
      };
      return icons[type] || "💧";
    };

    const getConcernIcon = (name) => {
      const icons = {
        "Acne": "🔴",
        "Aging": "⏳",
        "Hyperpigmentation": "🎯",
        "Sensitivity": "🌸",
        "Dryness": "🏜️",
        "Dullness": "✨",
        "Large Pores": "🔍",
        "Redness": "🌡️"
      };
      return icons[name] || "💧";
    };

    return (
      <Card className="card-product rounded-3xl overflow-hidden" data-testid="skin-profile-card">
        <CardHeader className="pb-4 bg-gradient-to-r from-primary/5 to-pink-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-pink-200/50 flex items-center justify-center">
                <Droplets className="w-5 h-5 text-primary" />
              </div>
              <CardTitle className="font-serif text-xl">Your Skin Profile</CardTitle>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setShowSkinAssessment(true)}
              className="text-muted-foreground hover:text-primary"
            >
              <Settings2 className="w-4 h-4 mr-1" />
              Edit
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="space-y-4">
            {/* Skin Type */}
            <div className="flex items-center gap-3">
              <span className="text-2xl">{getSkinTypeIcon(skinProfile.skin_type)}</span>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Skin Type</p>
                <p className="font-medium text-primary capitalize">{skinProfile.skin_type || "Not set"}</p>
              </div>
            </div>
            
            {/* Skin Concerns */}
            {skinProfile.concerns && skinProfile.concerns.length > 0 ? (
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">
                  <Target className="w-3 h-3 inline mr-1" />
                  Your Goals
                </p>
                <div className="flex flex-wrap gap-2">
                  {skinProfile.concerns.map((concern, idx) => (
                    <Badge 
                      key={concern.id} 
                      variant="secondary"
                      className={`${
                        idx === 0 ? 'bg-primary/10 text-primary' : 'bg-gray-100'
                      }`}
                    >
                      {getConcernIcon(concern.name)} {concern.name}
                    </Badge>
                  ))}
                </div>
              </div>
            ) : (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setShowSkinAssessment(true)}
                className="w-full"
              >
                <Target className="w-4 h-4 mr-2" />
                Add Skin Goals
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  const toggleShelve = async (productId, currentShelved) => {
    try {
      const newShelved = !currentShelved;
      await axios.put(`${API}/products/${productId}/shelve?shelved=${newShelved}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update the product in state
      setProducts(products.map(p => 
        p.id === productId ? { ...p, shelved: newShelved } : p
      ));
      
      toast.success(newShelved ? "Product shelved - excluded from routines" : "Product unshelved - back in rotation");
    } catch (error) {
      toast.error("Failed to update product");
    }
  };

  // Toggle pin status for AM/PM routine
  const togglePin = async (productId, timeOfDay, currentPinned, e) => {
    e.stopPropagation();
    try {
      const newPinned = !currentPinned;
      const response = await axios.put(
        `${API}/products/${productId}/pin?time_of_day=${timeOfDay}&pinned=${newPinned}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setProducts(products.map(p => 
        p.id === productId 
          ? { ...p, is_pinned_am: response.data.is_pinned_am, is_pinned_pm: response.data.is_pinned_pm }
          : p
      ));
      
      toast.success(
        newPinned 
          ? `Pinned to ${timeOfDay.toUpperCase()} routine` 
          : `Unpinned from ${timeOfDay.toUpperCase()} routine`
      );
    } catch (error) {
      toast.error("Failed to update pin status");
    }
  };

  const RoutineCard = ({ title, icon: Icon, steps, time }) => (
    <Card className="card-product rounded-3xl overflow-hidden" data-testid={`routine-card-${time}`}>
      <CardHeader className={`pb-4 ${time === 'am' ? 'bg-amber-50/50' : 'bg-indigo-50/50'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${time === 'am' ? 'bg-amber-100' : 'bg-indigo-100'}`}>
              <Icon className={`w-5 h-5 ${time === 'am' ? 'text-amber-600' : 'text-indigo-600'}`} />
            </div>
            <CardTitle className="font-serif text-xl">{title}</CardTitle>
          </div>
          <Badge className={time === 'am' ? 'badge-am' : 'badge-pm'}>
            {steps?.length || 0} steps
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        {steps && steps.length > 0 ? (
          <div className="space-y-3">
            {steps.map((step, idx) => {
              const product = products.find(p => p.id === step.product_id);
              const isShelved = product?.shelved;
              const skiniqScore = product?.skiniq_score;
              const hasScore = skiniqScore !== null && skiniqScore !== undefined;
              const isPinnedAm = product?.is_pinned_am ?? false;
              const isPinnedPm = product?.is_pinned_pm ?? false;
              
              return (
                <div 
                  key={idx} 
                  className={`routine-step flex items-start gap-4 p-3 rounded-lg hover:bg-primary/5 transition-colors cursor-pointer ${isShelved ? 'opacity-50' : ''}`}
                  data-testid={`routine-step-${time}-${idx}`}
                >
                  <div 
                    className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm"
                    onClick={() => navigate(`/products?highlight=${step.product_id}`)}
                  >
                    {step.order || idx + 1}
                  </div>
                  <div 
                    className="flex-1 min-w-0"
                    onClick={() => navigate(`/products?highlight=${step.product_id}`)}
                  >
                    <p className="font-medium text-sm leading-tight">{step.product_name}</p>
                    {/* Brand + SkinIQ Score */}
                    <div className="flex items-center gap-2 mt-1">
                      {(step.brand || product?.brand) && (
                        <span className="text-xs font-semibold text-primary/70 uppercase tracking-wide">
                          {step.brand || product?.brand}
                        </span>
                      )}
                      {/* SkinIQ Score Badge with Breakdown Tooltip */}
                      {!isShelved && hasScore && (
                        <ScoreBreakdown 
                          score={skiniqScore} 
                          isBanned={product?.is_banned}
                          size="small"
                        />
                      )}
                      {!isShelved && !hasScore && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium text-gray-500 bg-gray-100">
                          N/A
                        </span>
                      )}
                    </div>
                    {/* Action Icons - New Line */}
                    <div className="flex items-center gap-2 mt-2">
                      {!isShelved && product && (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              togglePin(product.id, 'am', isPinnedAm, e);
                            }}
                            className={`p-1.5 rounded-md transition-all ${
                              isPinnedAm 
                                ? 'bg-amber-100 ring-2 ring-amber-300' 
                                : 'hover:bg-amber-50'
                            }`}
                            title={isPinnedAm ? "Unpin from AM" : "Pin to AM"}
                          >
                            <Sun className={`w-4 h-4 text-amber-500 ${isPinnedAm ? 'fill-amber-300' : ''}`} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              togglePin(product.id, 'pm', isPinnedPm, e);
                            }}
                            className={`p-1.5 rounded-md transition-all ${
                              isPinnedPm 
                                ? 'bg-indigo-100 ring-2 ring-indigo-300' 
                                : 'hover:bg-indigo-50'
                            }`}
                            title={isPinnedPm ? "Unpin from PM" : "Pin to PM"}
                          >
                            <Moon className={`w-4 h-4 text-indigo-500 ${isPinnedPm ? 'fill-indigo-300' : ''}`} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleShelve(product.id, product.shelved);
                            }}
                            className="p-1.5 rounded-md text-gray-500 hover:bg-pink-500 hover:text-white transition-all duration-150"
                            title="Shelve product (out of stock)"
                          >
                            <Archive className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      {isShelved && (
                        <>
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5 bg-gray-200 text-gray-600">
                            <Archive className="w-3 h-3 mr-1" />
                            Shelved
                          </Badge>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleShelve(product.id, product.shelved);
                            }}
                            className="p-1.5 rounded-md text-primary hover:bg-primary hover:text-white transition-all duration-150"
                            title="Unshelve product"
                          >
                            <RotateCcw className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-1" />
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No {time === 'am' ? 'morning' : 'evening'} routine yet</p>
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F7]">
        <Navbar />
        <div className="flex items-center justify-center py-32">
          <div className="animate-pulse-soft text-center">
            <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto">
              <div className="w-8 h-8 rounded-full bg-primary/40"></div>
            </div>
            <p className="mt-4 text-muted-foreground">Loading your routine...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="dashboard">
      <Navbar />
      
      {/* Skin Assessment Modal */}
      {showSkinAssessment && (
        <SkinAssessment 
          onComplete={handleSkinAssessmentComplete} 
          onClose={() => setShowSkinAssessment(false)}
          initialSkinType={user?.skin_type}
        />
      )}

      <main className="container mx-auto px-6 py-8">
        {/* Welcome Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 animate-fade-in-up">
          <div className="flex items-center gap-4">
            <Avatar className="w-14 h-14 border-2 border-white shadow-lg">
              <AvatarImage src={user?.profile_image_url} />
              <AvatarFallback className="bg-primary text-white text-lg">
                {user?.name?.charAt(0)?.toUpperCase() || "U"}
              </AvatarFallback>
            </Avatar>
            <div>
              <h1 className="font-serif text-2xl lg:text-3xl text-primary">
                Welcome back, {user?.name?.split(' ')[0]}
              </h1>
              <div className="flex flex-wrap items-center gap-3 mt-1">
                <p className="text-muted-foreground text-sm">
                  {user?.skin_type ? (
                    <span className="capitalize">{user.skin_type} skin • </span>
                  ) : null}
                  {products.length} products in library
                </p>
                <StreakWidget />
              </div>
            </div>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={generateRoutine}
              disabled={generating}
              className="btn-outline"
              data-testid="generate-routine-btn"
            >
              {generating ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              {generating ? "Generating..." : "Generate Routine"}
            </Button>
            <Button 
              onClick={() => navigate("/add-product")}
              className="btn-primary"
              data-testid="add-product-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Product
            </Button>
          </div>
        </div>

        {/* Main Content Grid - Bento Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* AM Routine */}
          <div className="lg:col-span-1 animate-fade-in-up stagger-1">
            <RoutineCard 
              title="Morning Routine" 
              icon={Sun} 
              steps={routine?.am_routine} 
              time="am"
            />
          </div>

          {/* PM Routine */}
          <div className="lg:col-span-1 animate-fade-in-up stagger-2">
            <RoutineCard 
              title="Evening Routine" 
              icon={Moon} 
              steps={routine?.pm_routine} 
              time="pm"
            />
          </div>

          {/* Quick Stats & Tips */}
          <div className="lg:col-span-1 space-y-6 animate-fade-in-up stagger-3">
            {/* Skin Profile Card */}
            <SkinProfileCard />
            
            {/* Product Status Summary */}
            <Card className="card-product rounded-3xl">
              <CardHeader className="pb-2">
                <CardTitle className="font-serif text-lg flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Package className="w-4 h-4 text-primary" />
                  </div>
                  Product Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-3">
                  <button 
                    onClick={() => navigate("/products?status=keep")}
                    className="text-center p-3 rounded-xl bg-green-50 hover:bg-green-100 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                    </div>
                    <p className="text-2xl font-bold text-green-600">
                      {products.filter(p => p.status === 'keep').length}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">Keep</p>
                  </button>
                  <button 
                    onClick={() => navigate("/products?status=alternate")}
                    className="text-center p-3 rounded-xl bg-amber-50 hover:bg-amber-100 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <RefreshCw className="w-4 h-4 text-amber-600" />
                    </div>
                    <p className="text-2xl font-bold text-amber-600">
                      {products.filter(p => p.status === 'alternate').length}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">Alternate</p>
                  </button>
                  <button 
                    onClick={() => navigate("/products?shelved=true")}
                    className="text-center p-3 rounded-xl bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <Archive className="w-4 h-4 text-gray-500" />
                    </div>
                    <p className="text-2xl font-bold text-gray-500">
                      {products.filter(p => p.shelved).length}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">Shelved</p>
                  </button>
                </div>
                <Button 
                  variant="ghost" 
                  className="w-full mt-4 text-primary hover:bg-primary/5"
                  onClick={() => navigate("/products")}
                  data-testid="view-all-products-btn"
                >
                  View All Products
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
            {/* Tips Card */}
            {routine?.tips && routine.tips.length > 0 && (
              <Card className="card-product rounded-3xl border-green-200/50 bg-gradient-to-br from-green-50/50 to-white">
                <CardHeader className="pb-2">
                  <CardTitle className="font-serif text-lg flex items-center gap-2 text-green-700">
                    <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                      <Lightbulb className="w-4 h-4 text-green-600" />
                    </div>
                    Tips for You
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-32">
                    <div className="space-y-3">
                      {routine.tips.map((tip, idx) => (
                        <div key={idx} className="flex items-start gap-3 text-sm p-2 rounded-lg bg-white/60">
                          <span className="text-green-500 text-lg">✦</span>
                          <span className="text-muted-foreground">{tip}</span>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            )}

            {/* Warnings Card */}
            {routine?.warnings && routine.warnings.length > 0 && (
              <Card className="card-product rounded-3xl border-red-200/50 bg-gradient-to-br from-red-50/50 to-white mt-6">
                <CardHeader className="pb-2">
                  <CardTitle className="font-serif text-lg flex items-center gap-2 text-red-700">
                    <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                      <AlertTriangle className="w-4 h-4 text-red-600" />
                    </div>
                    Warnings
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {routine.warnings.map((warning, idx) => (
                      <div key={idx} className="flex items-start gap-3 text-sm p-2 rounded-lg bg-white/60">
                        <span className="text-red-500 text-lg">⚠</span>
                        <span className="text-red-700/80">{warning}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recommended For You Card - Link to Full Page */}
            <Card 
              className="card-product rounded-3xl border-purple-200/50 bg-gradient-to-br from-purple-50/50 to-white mt-6 cursor-pointer hover:shadow-md transition-all" 
              data-testid="recommendations-card"
              onClick={() => navigate("/recommendations")}
            >
              <CardContent className="p-5">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-6 h-6 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-serif text-lg text-purple-700">Recommended For You</h3>
                    <p className="text-sm text-muted-foreground">
                      {recommendations.length} products picked for your {skinProfile?.skin_type || 'skin'} type
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-purple-400" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Empty State */}
        {products.length === 0 && (
          <div className="text-center py-16 animate-fade-in-up">
            <div className="w-20 h-20 rounded-full bg-secondary flex items-center justify-center mx-auto mb-6">
              <Package className="w-10 h-10 text-primary/50" />
            </div>
            <h3 className="font-serif text-2xl text-primary mb-2">No Products Yet</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Start by adding your skincare products to get personalized routine recommendations.
            </p>
            <Button 
              onClick={() => navigate("/add-product")}
              className="btn-primary"
              data-testid="empty-add-product-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Product
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
